/**
 * Cookie Utilities for Cookie Consent Management
 * 
 * Handles reading and writing consent preferences to a secure cookie
 */

import { logger } from './logger';

export interface ConsentPreferences {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
}

// Default consent state - only necessary cookies are enabled by default
export const DEFAULT_CONSENT: ConsentPreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

// Cookie name for storing consent preferences
const CONSENT_COOKIE_NAME = 'nova_cookie_consent';

// Cookie expiration time (1 year)
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Read consent preferences from cookie
 * @returns ConsentPreferences object or null if no cookie exists
 */
export const readConsentCookie = (): ConsentPreferences | null => {
  try {
    // Parse all cookies
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [name, value] = cookie.trim().split('=');
      acc[name] = value;
      return acc;
    }, {} as Record<string, string>);

    // Get our consent cookie
    const consentCookie = cookies[CONSENT_COOKIE_NAME];
    
    if (!consentCookie) {
      return null;
    }

    // Decode and parse JSON
    const decodedValue = decodeURIComponent(consentCookie);
    const preferences = JSON.parse(decodedValue) as ConsentPreferences;

    // Validate the structure
    if (
      typeof preferences === 'object' &&
      typeof preferences.necessary === 'boolean' &&
      typeof preferences.preferences === 'boolean' &&
      typeof preferences.analytics === 'boolean' &&
      typeof preferences.marketing === 'boolean'
    ) {
      // Ensure necessary is always true
      return {
        ...preferences,
        necessary: true,
      };
    }

    return null;
  } catch (error) {
    logger.warn('Failed to read consent cookie:', error);
    return null;
  }
};

/**
 * Write consent preferences to cookie
 * @param preferences ConsentPreferences object to store
 */
export const writeConsentCookie = (preferences: ConsentPreferences): void => {
  try {
    // Ensure necessary is always true
    const safePreferences: ConsentPreferences = {
      ...preferences,
      necessary: true,
    };

    // Encode the preferences as JSON
    const encodedValue = encodeURIComponent(JSON.stringify(safePreferences));

    // Calculate expiry date
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);

    // Create secure cookie with SameSite protection
    const cookieString = [
      `${CONSENT_COOKIE_NAME}=${encodedValue}`,
      `expires=${expiryDate.toUTCString()}`,
      'path=/',
      'SameSite=Strict',
      // Only set Secure flag in production (HTTPS)
      ...(window.location.protocol === 'https:' ? ['Secure'] : []),
    ].join('; ');

    document.cookie = cookieString;
  } catch (error) {
    logger.error('Failed to write consent cookie:', error);
  }
};

/**
 * Delete the consent cookie (for testing or reset purposes)
 */
export const deleteConsentCookie = (): void => {
  try {
    const cookieString = [
      `${CONSENT_COOKIE_NAME}=`,
      'expires=Thu, 01 Jan 1970 00:00:00 UTC',
      'path=/',
      'SameSite=Strict',
    ].join('; ');

    document.cookie = cookieString;
  } catch (error) {
    logger.error('Failed to delete consent cookie:', error);
  }
};

/**
 * Check if consent has been given for a specific category
 * @param category The consent category to check
 * @returns boolean indicating if consent has been given
 */
export const hasConsent = (category: keyof ConsentPreferences): boolean => {
  const preferences = readConsentCookie();
  if (!preferences) {
    return category === 'necessary'; // Only necessary cookies are allowed by default
  }
  return preferences[category];
};
